"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Search, Phone, Eye, Truck, ChevronLeft, ChevronRight, RefreshCw, MapPin, Clock, Store, User, Plus, LayoutGrid, List } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { deliveryOrdersAPI, captainAPI } from "../services/api"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LiveOrderMap from "../components/LiveOrderMap"
import PhoneOrderWizard from "../components/PhoneOrderWizard"
import OrdersKanban from "../components/orders/OrdersKanban"

interface DeliveryOrder {
    _id: string
    orderNumber?: string
    status: string
    orderSource: string
    customer?: { _id: string; username: string; phoneNumber: string; fullName?: string }
    customerPhone?: string
    customerName?: string
    restaurant: { _id: string; name: string; logo?: string }
    captain?: { _id: string; name: string; phone: string }
    items: Array<{ foodItem: { name: string }; quantity: number; price: number; size?: string }>
    pricing: { subtotal: number; deliveryFee: number; total: number }
    deliveryAddress: { street?: string; city?: string; fullAddress?: string } | string
    specialInstructions?: string
    createdAt: string
}


interface Captain {
    _id: string
    name: string
    phone: string
    isOnline: boolean
}

const AdminDeliveryOrdersPage: React.FC = () => {
    const { t, isRTL } = useLanguage()
    const { toast } = useToast()

    const [orders, setOrders] = useState<DeliveryOrder[]>([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState<string>("all")
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalOrders, setTotalOrders] = useState(0)
    const [showPhoneOrderWizard, setShowPhoneOrderWizard] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState<DeliveryOrder | null>(null)
    const [showAssignModal, setShowAssignModal] = useState(false)
    const [availableCaptains, setAvailableCaptains] = useState<Captain[]>([])

    const [assigningCaptain, setAssigningCaptain] = useState(false)
    const [viewMode, setViewMode] = useState<"list" | "kanban">("list")

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true)
            const params: any = { page: currentPage, limit: 20 }
            if (searchQuery) params.search = searchQuery
            if (statusFilter !== "all") params.status = statusFilter

            const response = await deliveryOrdersAPI.getDeliveryOrders(params)
            if (response.data.success) {
                setOrders(response.data.data.orders || [])
                setTotalPages(response.data.data.pagination?.totalPages || 1)
                setTotalOrders(response.data.data.pagination?.totalOrders || 0)
            }
        } catch (error) {
            console.error("Failed to fetch orders:", error)
            toast({ title: t("error"), description: t("failedToLoadOrders"), variant: "destructive" })
        } finally {
            setLoading(false)
        }
    }, [currentPage, searchQuery, statusFilter, t, toast])

    const fetchAvailableCaptains = async () => {
        try {
            const response = await deliveryOrdersAPI.getAvailableCaptains()
            setAvailableCaptains(response.data.data || response.data.captains || [])
        } catch (error) {
            console.error("Failed to fetch captains:", error)
        }
    }

    useEffect(() => {
        fetchOrders()
    }, [fetchOrders])

    const handleAssignCaptain = async (captainId: string) => {
        if (!selectedOrder) return
        try {
            setAssigningCaptain(true)
            await deliveryOrdersAPI.assignCaptain(selectedOrder._id, captainId)
            toast({ title: t("success"), description: t("captainAssigned") })
            setShowAssignModal(false)
            setSelectedOrder(null)
            fetchOrders()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToAssignCaptain"), variant: "destructive" })
        } finally {
            setAssigningCaptain(false)
        }
    }

    const handleStatusUpdate = async (orderId: string, newStatus: string) => {
        try {
            await deliveryOrdersAPI.updateOrderStatus(orderId, newStatus)
            toast({ title: t("success"), description: t("orderStatusUpdated") })
            fetchOrders()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToUpdateStatus"), variant: "destructive" })
        }
    }

    const openAssignModal = (order: DeliveryOrder) => {
        setSelectedOrder(order)
        fetchAvailableCaptains()
        setShowAssignModal(true)
    }

    const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SDG" }).format(amount)
    const formatDate = (dateString: string) => new Date(dateString).toLocaleString()

    const getStatusColor = (status: string) => {
        switch (status) {
            case "delivered": return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            case "cancelled": return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            case "pending": return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
            case "on-the-way": return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            case "preparing": return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
            default: return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
        }
    }

    const getAddressString = (address: DeliveryOrder["deliveryAddress"]) => {
        if (typeof address === "string") return address
        return address?.fullAddress || `${address?.street || ""}, ${address?.city || ""}` || t("noAddress")
    }

    const statusOptions = [
        { value: "all", label: t("allStatus") },
        { value: "pending", label: t("pending") },
        { value: "confirmed", label: t("confirmed") },
        { value: "preparing", label: t("preparing") },
        { value: "ready", label: t("ready") },
        { value: "picked-up", label: t("pickedUp") },
        { value: "on-the-way", label: t("onTheWay") },
        { value: "delivered", label: t("delivered") },
        { value: "cancelled", label: t("cancelled") },
    ]

    return (
        <div className="space-y-6">
            <PageHeader title={t("deliveryOrders")} subtitle={t("manageDeliveryOrders")} />
            <div className="space-y-6">
                {/* Live Order Map */}
                <LiveOrderMap className="h-[400px]" onOrderClick={(orderId) => {
                    const order = orders.find(o => o._id === orderId)
                    if (order) setSelectedOrder(order)
                }} />

                {/* Actions Bar */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="relative">
                            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                                placeholder={t("searchOrders")}
                                className={`${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-64`}
                            />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1) }}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <button onClick={fetchOrders} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        </button>
                        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setViewMode("list")}
                                className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-white dark:bg-gray-700 shadow-sm text-red-600" : "text-gray-500 hover:text-gray-900 dark:text-gray-400"}`}
                                title={t("listView")}
                            >
                                <List className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode("kanban")}
                                className={`p-1.5 rounded-md transition-all ${viewMode === "kanban" ? "bg-white dark:bg-gray-700 shadow-sm text-red-600" : "text-gray-500 hover:text-gray-900 dark:text-gray-400"}`}
                                title={t("kanbanView")}
                            >
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowPhoneOrderWizard(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        <Phone className="w-4 h-4" />
                        {t("newPhoneOrder")}
                    </button>
                </div>

                {/* Orders Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-12">
                            <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">{t("noDeliveryOrdersFound")}</p>
                        </div>
                    ) : viewMode === "kanban" ? (
                        <div className="p-4">
                            <OrdersKanban
                                orders={orders}
                                onStatusUpdate={handleStatusUpdate}
                                onOrderClick={openAssignModal}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("order")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("customer")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("restaurant")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("driver")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("status")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("total")}</th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${isRTL ? "text-right" : "text-left"}`}>{t("actions")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {orders.map((order) => (
                                            <tr key={order._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-4 py-3">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">#{order.orderNumber || order._id.slice(-6)}</p>
                                                        <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                                                        {order.orderSource === "phone_order" && (
                                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded mt-1">
                                                                <Phone className="w-3 h-3" /> {t("phoneOrder")}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-gray-400" />
                                                        <div>
                                                            <p className="text-gray-900 dark:text-white">{order.customer?.fullName || order.customer?.username || order.customerName || t("guest")}</p>
                                                            <p className="text-xs text-gray-500">{order.customer?.phoneNumber || order.customerPhone}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Store className="w-4 h-4 text-gray-400" />
                                                        <span className="text-gray-900 dark:text-white">{order.restaurant?.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {order.captain ? (
                                                        <div className="flex items-center gap-2">
                                                            <Truck className="w-4 h-4 text-green-500" />
                                                            <div>
                                                                <p className="text-gray-900 dark:text-white">{order.captain.name}</p>
                                                                <p className="text-xs text-gray-500">{order.captain.phone}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => openAssignModal(order)}
                                                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                                                        >
                                                            <Plus className="w-3 h-3" /> {t("assignDriver")}
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                                        {t(order.status)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(order.pricing?.total || 0)}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setSelectedOrder(order)}
                                                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-sm text-gray-500">{t("showingOf", { count: orders.length, total: totalOrders })}</p>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border disabled:opacity-50">
                                        {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                                    </button>
                                    <span className="text-sm">{currentPage} / {totalPages}</span>
                                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border disabled:opacity-50">
                                        {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Phone Order Wizard */}
            {showPhoneOrderWizard && (
                <PhoneOrderWizard onClose={() => setShowPhoneOrderWizard(false)} onSuccess={fetchOrders} />
            )}

            {/* Assign Captain Modal */}
            {showAssignModal && selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-4">{t("assignDriver")}</h3>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {availableCaptains.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">{t("noAvailableDrivers")}</p>
                            ) : (
                                availableCaptains.map((captain) => (
                                    <button
                                        key={captain._id}
                                        onClick={() => handleAssignCaptain(captain._id)}
                                        disabled={assigningCaptain}
                                        className="w-full p-3 flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Truck className="w-5 h-5 text-gray-400" />
                                            <div className="text-left">
                                                <p className="font-medium">{captain.name}</p>
                                                <p className="text-sm text-gray-500">{captain.phone}</p>
                                            </div>
                                        </div>
                                        {captain.isOnline && <span className="w-2 h-2 bg-green-500 rounded-full" />}
                                    </button>
                                ))
                            )}
                        </div>
                        <div className="flex justify-end mt-4">
                            <button onClick={() => { setShowAssignModal(false); setSelectedOrder(null) }} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                                {t("cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminDeliveryOrdersPage
